import Book from '@mui/icons-material/Book';
import { FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import React, { Fragment, useEffect } from 'react';
import { userRequest } from '../../auth/requestMethods';
import dateFormat from '../../utils/dateFormat';
import Breadcrumb from '../common/breadcrumb';
import DateRangeComponent from '../dateRange';
import FilterAccordian from '../filterAccordian';
import CustomTable from '../tables/MuiDataGrid';
import { formatDate } from '../../helper/formatDate';

const Manage = ({ history }) => {
  useEffect(() => {
    document.title = 'PFSA | Exceptions | Manage';
  }, []);
  const [perPage, setPerPage] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [dataIsFetching, setDataIsFetching] = React.useState(true);
  const [appliedFilters, setAppliedFilters] = React.useState(['is_active']);
  const [schoolsFilter, setSchoolsFilter] = React.useState([]);
  const [tableData, setTableData] = React.useState({
    count: 0,
    exceptions: [],
  });
  const [searchInput, setSearchInput] = React.useState({
    school: 'All',
    type: 'All',
    to_date: '',
    from_date: '',
    total_count: '',
    is_active: 'All',
  });

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setDataIsFetching(true);

        let queryString = `/exceptions?per_page=${perPage}&page=${page}&${getQueryString()}`;

        queryString &&
          userRequest()
            .get(queryString)
            .then(({ data }) => {
              setTableData({
                count: data.total,
                exceptions: data.exceptions,
              });

              setDataIsFetching(false); // Added to set fetching to false after update
            })
            .catch((error) => {
              console.error(error.response);
              setDataIsFetching(false); // Added to set fetching to false in case of error
            });
      } catch (error) {
        console.error(error.response);
        setDataIsFetching(false); // Added to set fetching to false in case of error
      }
    };

    fetchData();
  }, [perPage, page, searchInput]);

  React.useEffect(() => {
    userRequest()
      .get('/schools/names')
      .then(({ data }) => {
        setSchoolsFilter(data.schools);
      })
      .catch(({ response }) => console.log(response));
  }, []);

  /**
   * Updates the status of an exception and reflects the change in the table data.
   *
   * @param {number} id - The ID of the exception to update.
   * @param {Object} data - The data to update the exception with.
   *
   * @returns {void} Updates the table data and logs any errors.
   */
  function setStatus(id, data) {
    userRequest()
      .post(`/exceptions/update-is-active/${id}`, data)
      .then(() => {
        // Fetch updated data from the server
        userRequest()
          .get(`/exceptions?per_page=${perPage}&page=${page}&${getQueryString()}`)
          .then(({ data }) => {
            setTableData({
              count: data.total,
              exceptions: data.exceptions,
            });
          });
      })
      .catch((error) => {
        if (error.response) {
          console.error('Error in setStatus:', error.response.data);
        } else {
          console.error('Error in setStatus:', error.message);
        }
      });
  }

  function meetFilterConditions(data, isUpdate = true) {
    if (!isUpdate) {
      if (tableData.count === perPage) return false;
    }
    if (appliedFilters.length > 1) {
      return appliedFilters.every((key) => searchInput[key] === data[key]);
    }
    return true;
  }

  const actionMethods = {
    /**
     * Navigates to the edit page for the given exception ID.
     *
     * @param {Object} row - The row data from the table.
     *
     * @returns {void} Redirects to the edit page.
     */
    toggleEdit: (row) => {
      history.push(`${process.env.PUBLIC_URL}/exceptions/edit/${row.id}`);
    },

    /**
     * Toggles the is_active status of the given exception ID and updates the table with the new data.
     *
     * @param {Object} row - The row data from the table.
     *
     * @returns {void} Updates the table data and sets the loading status to false.
     */
    toggleActive: (row) => {
      setStatus(row.id, { is_active: !row.is_active });
      userRequest()
        .get(`/exceptions?per_page=${perPage}&page=${page}&${getQueryString()}`)
        .then(({ data }) => {
          setTableData({
            count: data.total,
            exceptions: data.exceptions,
          });
          setDataIsFetching(false);
        })
        .catch((error) => {
          console.error(error.response);
          setDataIsFetching(false);
        });
    },
  };

  /**
   * Handles the search input form submission, updates the table data with the
   * filtered results, and logs any errors.
   *
   * @param {Event} e - The form submission event.
   *
   * @returns {void} Updates the table data and logs any errors.
   */
  const onSearch = (e) => {
    e.preventDefault();
    let foundFilter = [];
    const queryString = getQueryString(foundFilter);
    setAppliedFilters(foundFilter);

    setAppliedFilters(foundFilter);
    userRequest()
      .get(`/exceptions?per_page=${perPage}&page=${page}&${queryString}`)
      .then(({ data }) => {
        setTableData({
          count: data.total,
          exceptions: data.exceptions,
        });
      })
      .catch(({ response }) => {
        console.log(response);
      });
  };

  const columns = [
    {
      field: 'schools',
      headerName: 'School',
      flex: 1,
      valueGetter: ({ row }) => {
        return row.schools || row.school || 'N/A';
      },
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
      valueFormatter: ({ value }) =>
        value
          .split('_')
          .map((v) => v.charAt(0).toUpperCase() + v.slice(1, 100).toLowerCase())
          .join(' '),
    },
    {
      field: 'from_date',
      headerName: 'From',
      flex: 1,
      valueFormatter: ({ value }) => dateFormat(new Date(value?.slice(0, 10))),
    },
    {
      field: 'to_date',
      headerName: 'To',
      flex: 1,
      valueFormatter: ({ value }) => dateFormat(new Date(value?.slice(0, 10))),
    },
    {
      field: 'temporary_learners_fraction',
      headerName: 'Learner Count',
      flex: 1,
      type: 'number',
      valueFormatter: ({ value }) => (value ? value + ' %' : ''),
    },
    {
      field: 'sku_types',
      headerName: 'SKU Types',
      flex: 1,
      type: 'number',
      valueFormatter: ({ value }) => (value ? value : ''),
    },
    {
      field: 'menu',
      headerName: 'Menu',
      flex: 1,
    },
    {
      field: 'is_active',
      headerName: 'Status',
      headerAlign: 'center',
      width: 110,
      renderCell: ({ row }) =>
        row.is_active ? (
          <Typography className="font-success mx-auto">Active</Typography>
        ) : (
          <Typography className="font-danger mx-auto">Inactive</Typography>
        ),
    },
  ];

  /**
   * Handles the input change event and updates the search input state.
   *
   * @param {Event} e - The input change event object.
   * @param {any} val - The input change value.
   * @param {String} name - The name of the input field.
   *
   * @returns {void} Updates the search input state.
   */
  const onChangeInput = (e, val, name = null) => {
    if (name) {
      setSearchInput({ ...searchInput, [name]: val });
    } else {
      setSearchInput({ ...searchInput, [e.target.name]: e.target.value });
    }
  };

  /**
   * Converts the current search input values into a query string.
   *
   * @param {Array} foundFilter - An array to store the found filter keys.
   *
   * @returns {String} The query string.
   */
  function getQueryString() {
    const queryParams = Object.entries(searchInput)
      .filter(([key, value]) => value !== '' && value !== 'All') // Exclude empty or 'All' values
      .map(([key, value]) => {
        if (key === 'is_active') {
          // Handle special cases like 'is_active'
          value = value === 'ACTIVE' ? true : value === 'INACTIVE' ? false : '';
        }
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      });

    const queryString = queryParams.join('&'); // Join valid params with '&'
    return queryString;
  }

  function getFilters() {
    const selectedFilters = Object.entries(searchInput)
      .map(([key, value]) => {
        if (value !== '') {
          switch (key) {
            case 'school':
              if (value === 'All') {
                return [];
              } else {
                return `School: ${value}`;
              }
            case 'is_active':
              if (value === 'All') {
                return [];
              } else if (value === 'INACTIVE') {
                return `Status: Inactive`;
              }
              return `Status: Active`;
            case 'type':
              if (value === 'All') {
                return [];
              } else {
                return `Type: ${value
                  .split('_')
                  .map((v) => v.charAt(0).toUpperCase() + v.slice(1).toLowerCase())
                  .join(' ')}`;
              }
            case 'from_date':
              return `From Date: ${formatDate(value)}`;
            case 'to_date':
              return `To Date: ${formatDate(value)}`;

            default:
              return [];
          }
        }
        return [];
      })
      .filter((x) => x !== undefined && x.length !== 0);

    return selectedFilters.length > 0 ? selectedFilters : [];
  }

  const onDateRangeChange = (fromDate, toDate) => {
    if (fromDate && toDate) {
      setSearchInput((prev) => ({
        ...prev,
        from_date: fromDate,
        to_date: toDate,
      }));
    } else if (fromDate) {
      setSearchInput((prev) => ({
        ...prev,
        from_date: fromDate,
        to_date: '',
      }));
    } else if (toDate) {
      setSearchInput((prev) => ({
        ...prev,
        from_date: toDate,
        to_date: '',
      }));
    } else {
      setSearchInput((prev) => ({
        ...prev,
        from_date: '',
        to_date: '',
      }));
    }
  };

  return (
    <Fragment>
      <Breadcrumb icon={<Book />} title="Manage" parent="Exceptions" />

      <div className="container-fluid">
        <FilterAccordian filters={getFilters()}>
          <form className="theme-form">
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel>School</InputLabel>
                <Select
                  name="school"
                  value={searchInput.school}
                  onChange={(e) => onChangeInput(e, e.target.value, 'school')}
                  label="School"
                  style={{
                    fontStyle: searchInput.school === 'All' ? 'italic' : 'normal',
                  }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {schoolsFilter.map((val, i) => (
                    <MenuItem key={i} value={val.display_name}>
                      {val.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel>Exception Type</InputLabel>
                <Select
                  name="type"
                  value={searchInput.type}
                  onChange={(e) => onChangeInput(e, e.target.value, 'type')}
                  label="Exception Type"
                  style={{
                    fontStyle: searchInput.type === 'All' ? 'italic' : 'normal',
                  }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {['LEARNER_REDUCTION', 'MENU_SKU_TYPES', 'TEMPORARY_MENU'].map((val, i) => (
                    <MenuItem key={i} value={val}>
                      {val
                        .split('_')
                        .map((v) => v.charAt(0).toUpperCase() + v.slice(1, 100).toLowerCase())
                        .join(' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row m-0">
              <DateRangeComponent
                from={searchInput.from_date}
                to={searchInput.to_date}
                fromOnChange={(val) => setSearchInput((prev) => ({ ...prev, from_date: val }))}
                toOnChange={(val) => setSearchInput((prev) => ({ ...prev, to_date: val }))}
                onDateRangeChange={onDateRangeChange}
              />
            </div>
            <br />

            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel>Status</InputLabel>
                <Select
                  name="is_active"
                  value={searchInput.is_active}
                  onChange={(e) => onChangeInput(e, e.target.value, 'is_active')}
                  label="Status"
                  style={{
                    fontStyle: searchInput.is_active === 'All' ? 'italic' : 'normal',
                  }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  <MenuItem value={'ACTIVE'}>Active</MenuItem>
                  <MenuItem value={'INACTIVE'}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </div>
          </form>
        </FilterAccordian>
      </div>
      <button
        onClick={() => {
          history.push(`${process.env.PUBLIC_URL}/exceptions/create`);
        }}
        className="btn btn-secondary m-15"
        type="submit"
      >
        <i className="fa fa-plus fa-xl" aria-hidden="true"></i>
      </button>
      <div className="container-fluid">
        <CustomTable
          rows={tableData.exceptions}
          actions={actionMethods}
          columns={columns}
          setPageSize={setPerPage}
          setPage={setPage}
          rowCount={tableData.count}
          pageSize={perPage}
          loading={dataIsFetching}
          sortModel={[{ field: 'from_date', sort: 'asc' }]}
        />
      </div>
    </Fragment>
  );
};

export default Manage;
